import React, { useEffect, useState } from 'react';

import EventCartDesktop from './sections/EventCartDesktop';
import { TicketsContextProvider } from '@/contexts/tickets/TicketsContext';
import {
  useEvent,
  useTicketsSelected,
  usePayment,
  useBooking,
  useCheckout,
  useSaturated,
} from '@/store';
import useTicketsSelectionSummaryUpdate from '@/store/summary/useTicketSelectSummaryUpdate';
import EventCartMobile from './sections/EventCartMobile';
import useMobile from '@/hooks/useMobile';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';
import useEventContext from '@/contexts/event/useEventContext';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';

import FanPassDesktop from './sections/FanPassDesktop';
import FanPassMobile from './sections/FanPassMobile';
import SweepstakesEvent from './sections/SweepstakesEvent';
import { isEqualString } from '@/utils/validations';
import { eventHasMerch } from '@/config/onDemandMerchConfig';
import useLeavePage from '@/hooks/useLeavePage';
import useUserTracking from '@/hooks/userTracking';
import useCoreTracking from '@/hooks/userTracking/core';
import usePostMessages from '@/hooks/messages/usePostMessages';

export default function Home({ isMobile: isSsrMobile }) {
  const {
    event: {
      event: { id },
    },
  } = useEventContext();
  const [fanPass, setFanPass] = useState();

  const [isMobile, setIsMobile] = useState(isSsrMobile);
  const { isMobile: isCsrMobile } = useMobile();
  const {
    resetBooking,
    setStartDate,
    booking: { order },
  } = useBooking();
  const { isPreRegister } = useEvent();
  const { releaseBooking } = useCheckout();
  const { resetBookingPayment } = usePayment();
  const { removeAllTickets } = useTicketsSelected();
  const { requiredFanPass } = useBoletiaConfig();

  const { selectionAbandoned } = useUserTracking();
  const { trackLeavePageEvent } = useCoreTracking();

  useTicketsSelectionSummaryUpdate();

  const { eventDetailView } = useAnalyticsEvents();

  const { backButtonMessage, closeCheckoutMessage, onReceiveMessage } =
    usePostMessages();

  const { isEnabled, setStartTime, resetTries } = useSaturated();

  const onChangeFanPass = (pass) => {
    if (isEqualString(pass, fanPass)) {
      setFanPass(null);
    }
  };

  useLeavePage(() => {
    selectionAbandoned();
  });

  useEffect(() => {
    if (order) {
      releaseBooking(order);
      if (isEnabled) {
        resetTries();
      }
    }
    resetBooking();
    setStartDate();
    removeAllTickets();
    resetBookingPayment();
    setFanPass(requiredFanPass(id));
    backButtonMessage(false);
    const leaveMessage = onReceiveMessage((message) => {
      if (message === 'dataBeforeClose') {
        closeCheckoutMessage({
          ...trackLeavePageEvent(_, { step_name: 'ticket_selection' }, true),
        });
      }
    });

    if (eventHasMerch(id)) {
      eventDetailView(id);
    }
    return () => {
      leaveMessage();
    };
  }, []);

  useEffect(() => {
    setIsMobile(isCsrMobile);
    if (isEnabled) {
      setStartTime();
    }
  }, [isCsrMobile, isEnabled]);

  if (isPreRegister) {
    return <SweepstakesEvent />;
  }

  return (
    <TicketsContextProvider>
      {isMobile ? (
        <>
          {fanPass ? (
            <FanPassMobile onChange={onChangeFanPass} fanPass={fanPass} />
          ) : (
            <EventCartMobile />
          )}
        </>
      ) : (
        <>
          {fanPass ? (
            <FanPassDesktop onChange={onChangeFanPass} fanPass={fanPass} />
          ) : (
            <EventCartDesktop />
          )}
        </>
      )}
    </TicketsContextProvider>
  );
}
